import React, { useEffect, useState } from "react";
import { Container, Button, Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { getReviewBooking, postBookingReview, resetPostReviewFlags, selectPostBookingReviewStatus, selectReviewBooking, selectReviewBookingStatus } from "_redux/slices/analytics/review-control-panel";
import { fetchReviewedAirportsInfo, selectReviewAirportsInfo, selectReviewAirportsStatus } from "_redux/slices/main-screen/user";
import prmLogo from "_assets/images/PRM-Logo-PNG/logo.png"
import { CommentCard, QuestionCard } from "./questions-components";
import { CustomSpinner } from "_components/atoms";
import { PRM_LIGHT_BLUE_BG } from "_styles/colors";

const ReviewBooking = () => {

    const { bookingId } = useParams();
    //#region redux
    const dispatch = useDispatch();
    const reviewBooking = useSelector(selectReviewBooking);                                 //booking/trip for review
    const reviewBookingStatus = useSelector(selectReviewBookingStatus);                     //status of booking/trip for review.
    const airportsInfo = useSelector(selectReviewAirportsInfo);                             //object contains origin/destination airports info, key is iata
    const airportsInfoStatus = useSelector(selectReviewAirportsStatus);                     //status of geeting airports info.
    const postedReviewStatus = useSelector(selectPostBookingReviewStatus)                   // status of posting review, last step.
    //#endregion 

    //#region local state
    const [originAirportInfo, setOriginAirportInfo] = useState({});                          //origina airport info, contains name
    const [destinationAirportInfo, setDestinationAirportInfo] = useState({});                //destination airport info, contains name
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);                //screen height for mobile
    //states of airports questions
    const [qualityRatingArrival, setQualityRatingArrival] = useState(0);                     //quality of arrival airport
    const [qualityRatingDeparture, setQualityRatingDeparture] = useState(0);                 //quality of dep airport
    const [assistanceRatingArrival, setAssistanceRatingArrival] = useState(0);               //staff assistance  of arrival airport
    const [asssistanceRatingDeparture, setAssistanceRatingDeparture] = useState(0);          //staff assistance  of dep airport
    //states of airline questions
    const [qualityRatingAirline, setQualityRatingAirline] = useState(0);                     //quality of airline
    const [assistanceRatingAirline, setAssistanceRatingAirline] = useState(0);               //staff assistance of airline
    //waiting time, only for airports
    const [waitingTimeArrival, setWaitingTimeArrival] = useState("");                        //waiting time in arrival airport
    const [waitingTimeDeparture, setWaitingTimeDeparture] = useState("");                     //waiting time in departure airport
    //comments
    const [commentArrival, setCommentArrival] = useState(" ");                                //comments for arrival airport
    const [commentDeparture, setCommentDeparture] = useState(" ");                            //comments for dep airport
    const [airlineComment, setAirlineComment] = useState(" ");                                //comments for airline
    //buttons state (Start,Next,Submit)
    const [reviewStep, setReviewStep] = useState(1);
    const [enableNextBtn, setEnableNextBtn] = useState(false);
    const [enableSubmitBtn, setEnableSubmitBtn] = useState(false);
    const [submited, setSubmited] = useState(false)
    //#endregion 

    //#region useEffect 
    useEffect(() => {
        dispatch(getReviewBooking(bookingId)) // get booking for trip/review
        dispatch(resetPostReviewFlags())
    }, [])

    // height resize
    useEffect(() => {
        const handleResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    //getting origin/destination airports info to display airport name
    useEffect(() => {
        // anonymous visitors get a minimal booking: { _id, id, firstName, flightConfirmationDetails }
        if (reviewBooking && reviewBookingStatus === "succeeded") {
            const origin = reviewBooking?.flightConfirmationDetails?.origin,
                destination = reviewBooking?.flightConfirmationDetails?.destination;

            if (origin) dispatch(fetchReviewedAirportsInfo(origin))
            if (destination) dispatch(fetchReviewedAirportsInfo(destination))
        }
    }, [reviewBookingStatus]);

    useEffect(() => {

        if (
            !airportsInfo ||
            !reviewBooking ||
            !Object.keys(reviewBooking).length ||
            !reviewBooking.flightConfirmationDetails ||
            Object.keys(airportsInfo).length < 2
        ) return;

        // Check if trip has already been reviewed (only known to logged-in callers
        // who get the full booking; the anonymous shape has no status)
        if (reviewBooking?.reviewed || reviewBooking?.status === "reviewed") {
            setSubmited(true);
            return;
        }

        const { origin, destination } = reviewBooking.flightConfirmationDetails;

        if (airportsInfo[origin]) {
            setOriginAirportInfo(airportsInfo[origin]);
        }

        if (airportsInfo[destination]) {
            setDestinationAirportInfo(airportsInfo[destination]);
        }

    }, [airportsInfo, reviewBooking]);


    useEffect(() => {
        if (postedReviewStatus === "succeeded") {
            setReviewStep(4)
        }
    }, [postedReviewStatus])
    //condition to enable Next button, check if all questions about airports answered
    useEffect(() => {
        const allAnswered =
            qualityRatingArrival > 0 &&
            qualityRatingDeparture > 0 &&
            assistanceRatingArrival > 0 &&
            asssistanceRatingDeparture > 0 &&
            waitingTimeArrival !== "" &&
            waitingTimeDeparture !== "" &&
            commentArrival.trim() !== "" &&
            commentDeparture.trim() !== "";

        setEnableNextBtn(allAnswered);
    }, [
        qualityRatingArrival,
        qualityRatingDeparture,
        assistanceRatingArrival,
        asssistanceRatingDeparture,
        waitingTimeArrival,
        waitingTimeDeparture,
        commentArrival,
        commentDeparture
    ]);

    //condition to enable Submit button, check if all questions about airline answered
    useEffect(() => {
        const allAnswered =
            qualityRatingAirline > 0 &&
            assistanceRatingAirline > 0 &&
            airlineComment.trim() !== "";
        setEnableSubmitBtn(allAnswered);
    }, [qualityRatingAirline, assistanceRatingAirline, airlineComment]);
    //#endregion

    //#region functions/handlers
    //submit review
    const handleSubmitReview = () => {
        const flight = reviewBooking?.flightConfirmationDetails || {};
        const review = {
            bookingId: reviewBooking?.id ?? reviewBooking?._id,
            // not in the anonymous booking shape; present only for logged-in callers
            userId: reviewBooking?.userId,
            departure: {
                details: {
                    iata: flight.origin
                },
                review: {
                    quality: qualityRatingDeparture,
                    staff: asssistanceRatingDeparture,
                    comments: commentDeparture,
                    waitingTime: waitingTimeDeparture,
                }
            },
            arrival: {
                details: {
                    iata: flight.destination
                },
                review: {
                    quality: qualityRatingArrival,
                    staff: assistanceRatingArrival,
                    comments: commentArrival,
                    waitingTime: waitingTimeArrival,
                }
            },
            flight: {
                details: {
                    carrierCode: flight.carrierCode,
                    flightNumber: flight.flightNumber,
                    departureDate: flight.departureDate
                },
                review: {
                    boarding: qualityRatingAirline,
                    onboard: assistanceRatingAirline,
                    disembark: 1,
                    comments: airlineComment,
                }
            },
            portal: true
        }

        dispatch(postBookingReview(review))
    }
    //#endregion 

    //#region rendering 
    //initial screen
    if (reviewStep === 1) {
        return (
            <Container
                style={{
                    height: `${viewportHeight}px`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between", // push footer down
                    paddingTop: "20px",
                    paddingBottom: "20px",
                }}
            >
                {/* Header */}
                <Row className="align-items-center justify-content-between mb-4">
                    <Col xs="auto">
                        <h4 className="fw-bold mb-0">Trip Review</h4>
                    </Col>
                    <Col xs="auto">
                        <img
                            src={prmLogo}
                            alt="prmassist logo"
                            style={{ width: 60, height: 60 }}
                        />
                    </Col>
                </Row>

                {/* Description */}
                <Row style={{ flex: 1 }}>
                    <Col>
                        <p className="text-muted" style={{
                            maxWidth: 400, marginTop: "10px", fontSize: "1.2rem",
                            lineHeight: "1.6",
                        }}>
                            Help us improve airport and airline mobility assistance services
                            by submitting a survey on your trip experience.
                        </p>
                        {submited && (
                            <p className="text-success mt-3">
                                You have already submitted a review for this trip!
                            </p>
                        )}
                    </Col>
                </Row>

                {/* Footer button */}
                <Row>
                    <Col>
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-100"
                            onClick={() => setReviewStep(2)}
                            disabled={submited}
                        >
                            Review your Trip
                        </Button>
                    </Col>
                </Row>
            </Container>
        );
    }

    // Airports questions
    if (reviewStep === 2) {
        return (
            <Container className="p-4">
                {/* Airports questions... */}
                <QuestionCard
                    title="Assistance Quality"
                    text="Rate the quality of the assistance that you received during your airport journey."
                    departureAirport={originAirportInfo.name ?? ""}
                    arrivalAirport={destinationAirportInfo.name ?? ""}
                    departureRating={qualityRatingDeparture}
                    arrivalRating={qualityRatingArrival}
                    setDeparture={setQualityRatingDeparture}
                    setArrival={setQualityRatingArrival}
                    questions={2}
                />
                <QuestionCard
                    title="Staff Assistance"
                    text="Rate the  general helpfulness and courtesy of the airport staff in meeting your assistance needs"
                    departureAirport={originAirportInfo.name ?? ""}
                    arrivalAirport={destinationAirportInfo.name ?? ""}
                    departureRating={asssistanceRatingDeparture}
                    arrivalRating={assistanceRatingArrival}
                    setDeparture={setAssistanceRatingDeparture}
                    setArrival={setAssistanceRatingArrival}
                    questions={2}
                />

                <QuestionCard
                    title="Waiting Time"
                    text="Please select the waiting time for your assistance"
                    departureAirport={originAirportInfo.name ?? ""}
                    arrivalAirport={destinationAirportInfo.name ?? ""}
                    departureRating={waitingTimeDeparture}
                    arrivalRating={waitingTimeArrival}
                    setDeparture={setWaitingTimeDeparture}
                    setArrival={setWaitingTimeArrival}
                    questions={2}
                    customOptions={true}
                />

                <CommentCard
                    text="Please comment your overall experience with the airports"
                    departureAirport={originAirportInfo.name ?? ""}
                    arrivalAirport={destinationAirportInfo.name ?? ""}
                    setCommentDeparture={setCommentDeparture}
                    setCommentArrival={setCommentArrival}
                    commentArrival={commentArrival}
                    commentDeparture={commentDeparture}
                    isAirline={false}
                />
                <Row>
                    <Col>
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-100"
                            disabled={!enableNextBtn}
                            onClick={() => setReviewStep(3)}
                            style={{ marginBottom: "10px" }}
                        >
                            Next
                        </Button>
                    </Col>
                </Row>
            </Container>
        )
    }
    // Airline questions
    if (reviewStep === 3) {
        return (
            <Container className="p-4">

                {/* airline questions */}
                <QuestionCard
                    title="Assistance Quality"
                    text="Rate the quality of the assistance that was provided to you to help board and disembark."
                    departureRating={qualityRatingAirline}
                    setDeparture={setQualityRatingAirline}
                    questions={1}
                    airline={true}

                />
                <QuestionCard
                    title="Staff Assistance"
                    text="Rate the  general helpfulness and courtesy of the airline staff in meeting your assistance needs"
                    airline={true}
                    questions={1}
                    entityLabel="Emirates"
                    departureRating={assistanceRatingAirline}
                    setDeparture={setAssistanceRatingAirline}
                />
                <CommentCard
                    text="Please comment your overall experience with the airline"
                    setCommentDeparture={setAirlineComment}
                    airline={true}
                />

                <Row>
                    <Col>
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-100"
                            disabled={!enableSubmitBtn}
                            onClick={handleSubmitReview}
                            style={{ marginBottom: "20px" }}

                        >
                            {postedReviewStatus === "loading" ? <CustomSpinner /> : "Submit"}
                        </Button>
                    </Col>
                </Row>
            </Container>
        );
    }
    //last screen after submit
    if (reviewStep === 4) {
        return (
            <Container style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                marginTop: "10px"

            }}>
                <Row className="align-items-center justify-content-between">
                    <Col xs="auto">
                        <h4 className="fw-bold mb-0">Trip Review</h4>
                    </Col>
                    <Col xs="auto">
                        <img
                            src={prmLogo}
                            alt="prmassist logo"
                            style={{ width: 80, height: 80 }}
                        />
                    </Col>
                </Row>
                <Row className="mt-2 mb-4">
                    <Col>
                        <p className="text-muted" style={{ fontSize: "1.2rem", marginTop: "10px", maxWidth: 500 }}>
                            Thank you for submitting this review and for providing us with useful feedback to help improve passenger trips.
                        </p>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <p style={{ fontSize: "0.95rem", marginTop: "30px" }}>
                            Powered by <strong color={PRM_LIGHT_BLUE_BG}>PRMASSIST</strong>
                        </p>
                    </Col>
                </Row>
            </Container>)

    }
    //#endregion
};

export default ReviewBooking;